import { Context } from '@nuxt/types'
import { HTTP_REST_FRONTEND_API } from '../plugins/axiosFrontendApi'
import { ThemeType } from '../contracts/Settings'
import { PageHideNavigation } from '../contracts/Page'
import { checkQuotation } from '../api/bitrix/bApi/checkQuotation'
import { getLeader } from '../api/bitrix/brand-media/leader/leaderApi'
import { HTTP_REST } from '../plugins/axios'
import { API_ROUTES_ALL_OFFICES } from '../config/constants'
import { Registry } from '../helpers/registry'
import { searchVacanciesRequest } from './searchVacanciesRequest'
import { Logger } from './Logger'

let auxiliaryDataCache: { data: any; expireAt: number } | null = null
const AUXILIARY_CACHE_TTL = 30_000

export const pageRequest = async (ctx: Context) => {
  const {
    app,
    route: { path, query, fullPath },
    error,
    redirect,
    store: { dispatch, commit, getters },
    $config,
  } = ctx

  const reqUrl =
    path === '/search'
      ? $config.SM_API_GET_PAGE_STATE_PATH + fullPath
      : $config.SM_API_GET_PAGE_STATE_PATH + path

  const isProsto = /(?:^|\/)prosto(?:\/|$)/.test(path || '')
  const isInsurancePremium = path.startsWith('/insurance-premium-payment')

  try {
    const client = HTTP_REST_FRONTEND_API(ctx)

    // Level 1: все независимые запросы параллельно
    const pageDataPromise = client.get(reqUrl)
    const auxiliaryPromise = getAuxiliaryData(
      client,
      $config.SM_API_AUXILIARY_DATA,
      getters
    )
    const prostoPromise = isProsto ? getProstoData(ctx, client) : null
    const quotationPromise =
      isInsurancePremium && query?.quotationId
        ? checkQuotation(ctx, query.quotationId as string).catch((e) => {
            console.log('ERROR QUOTE:', e?.response?.data || e)
            return null
          })
        : null

    const [pageRes, auxiliaryData, prostoData, quotationResult] =
      await Promise.all([
        pageDataPromise,
        auxiliaryPromise,
        prostoPromise,
        quotationPromise,
      ])

    const content = pageRes.data

    if (content.redirect && content.redirect.length) {
      return redirect(301, content.redirect)
    }

    if (content.error) {
      error({ statusCode: 404 })
      commit('settings/SET_NOTIFICATION', undefined)
      return
    }

    if (isInsurancePremium) {
      if (
        !query?.quotationId ||
        !quotationResult?.data?.success ||
        quotationResult?.data?.status !== 'valid'
      ) {
        error({ statusCode: 404 })
        return
      }
    }

    let setThemeOnClient = false
    const {
      breadcrumbs,
      isSearchVacanciesPage,
      showButtonBuyOnline,
      components: pageComponents,
      theme = ThemeType.primary,
      notification,
      hideNavigation,
      hideBreadcrumbs,
      hideSurveyButton,
      surveyButtonNewDesign,
      showCategoriesMenu,
      showSearchBot,
      headerFooterNewDesign,
      mainSurveyText,
      mainSurveyButtonText,
      prostoArticleData,
      topMessage,
    } = content

    if (process.server) {
      commit('settings/SET_THEME', theme)
    } else {
      setThemeOnClient = true
    }

    // Level 2: запросы, зависящие от content, параллельно между собой
    const postContentPromises: Promise<void | unknown>[] = []

    if (isProsto && prostoData) {
      const { sections = [], tags = [] } = prostoData
      let sidebarItems = Array.isArray(sections)
        ? sections.slice()
        : Object.values(sections)
      sidebarItems = sidebarItems.sort((a, b) =>
        a.code === 'main' ? -1 : b.code === 'main' ? 1 : 0
      )
      commit('sections/SET_SIDEBAR_ITEMS', sidebarItems)

      if (prostoArticleData) {
        postContentPromises.push(
          getLeader(ctx, prostoArticleData.code, 3, prostoArticleData.id).then(
            (leader) => {
              const foundSection = sections.find(
                (s) => s.code === prostoArticleData.code
              )
              const recommendations = (leader.data?.data || []).map((item) => ({
                ...item,
                headingImg: foundSection?.img || null,
              }))
              const articleComponent = pageComponents[0]
              if (articleComponent && articleComponent.value?.article) {
                const tagsFull = tags.filter((item) =>
                  (articleComponent.value.article.tags || []).includes(
                    item.link
                  )
                )
                articleComponent.value.article = {
                  ...articleComponent.value.article,
                  recommendations,
                  tagsFull,
                }
              }
            }
          )
        )
      }
    }

    let searchResultPromise: Promise<{
      components?: any
      searchError?: boolean
    }> | null = null
    if (isSearchVacanciesPage) {
      searchResultPromise = searchVacanciesRequest(
        query,
        app,
        pageComponents,
        path === '/about/career'
      )
        .then(({ components }) => ({ components }))
        .catch((e) => {
          Logger.error(e)
          return { searchError: true as const }
        })
      postContentPromises.push(searchResultPromise)
    }

    const allOfficesSections = pageComponents.filter(
      (component) => component.type === 'sectionAllOffices'
    )
    if (allOfficesSections.length) {
      postContentPromises.push(
        HTTP_REST(ctx)
          .get(API_ROUTES_ALL_OFFICES)
          .then((response) => {
            const allOffices = response.data?.data || []
            allOfficesSections.forEach((item) => {
              item.value = { ...item.value, items: allOffices }
            })
          })
      )
    }

    if (postContentPromises.length) {
      await Promise.all(postContentPromises)
    }

    // Синхронные коммиты в store
    commit('pagesData/SET_PAGES_DATA_KEY', {
      key: 'auxiliaryData',
      data: auxiliaryData,
    })

    if (content.pageRoute) {
      const currentPage =
        content.pageRoute.find((page) => page.fullPath === path) ||
        content.pageRoute[0]

      const pageDate = new Date(currentPage.updatedAt)

      let componentsDate = pageDate
      if (Array.isArray(content.components) && content.components.length > 0) {
        for (const component of content.components as any[]) {
          if (component.updatedAt) {
            const compDate = new Date(component.updatedAt)
            if (compDate > componentsDate) {
              componentsDate = compDate
            }
          }
        }
      }

      const finalUpdatedDate =
        componentsDate > pageDate ? componentsDate : pageDate

      commit('settings/SET_UPDATED_TIME', finalUpdatedDate.toISOString())
    }

    dispatch('breadcrumbs/create', breadcrumbs || [])
    commit('breadcrumbs/SET_BREADCRUMBS_VISIBILITY', !hideBreadcrumbs)
    commit('popupBuyOnline/SET_SHOW_BUTTON', !!showButtonBuyOnline)
    commit('settings/SET_NOTIFICATION', notification)

    const isWebview = query.client === 'emp'
    commit('settings/SET_HIDE_FOOTER', isWebview)
    commit('settings/SET_HIDE_HEADER', isWebview)

    if (hideNavigation === PageHideNavigation.ALL) {
      commit('settings/SET_HIDE_FOOTER', true)
      commit('settings/SET_HIDE_HEADER', true)
    }

    if (hideNavigation === PageHideNavigation.FOOTER) {
      commit('settings/SET_HIDE_FOOTER', true)
    }

    if (hideNavigation === PageHideNavigation.HEADER) {
      commit('settings/SET_HIDE_HEADER', true)
    }

    const isIframeOnClearPage = pageComponents.find(
      (component) =>
        component.type === 'sectionIframe' && component.value.clearPage
    )
    if (isIframeOnClearPage) {
      commit('settings/SET_HIDE_FOOTER', true)
      commit('settings/SET_HIDE_HEADER', true)
      commit('settings/SET_HIDE_SURVEY_BTN', true)
    } else {
      commit('settings/SET_HIDE_SURVEY_BTN', hideSurveyButton)
    }

    commit('settings/SET_SURVEY_BTN_NEW_DESIGN', surveyButtonNewDesign)
    commit('settings/SET_SHOW_CATEGORIES_MENU', showCategoriesMenu)
    commit('settings/SET_SHOW_SEARCH_BOT', showSearchBot)
    commit('settings/SET_HEADER_FOOTER_NEW_DESIGN', headerFooterNewDesign)

    commit('settings/SET_MAIN_SURVEY', {
      text: mainSurveyText,
      buttonText: mainSurveyButtonText,
    })

    commit('settings/SET_TOP_MESSAGE', topMessage)

    if (isProsto) {
      commit('registry/REGISTRY_COMPONENT', Registry.BrandMediaDefaultLayout)
    }

    if (showSearchBot) {
      commit('registry/REGISTRY_COMPONENT', Registry.ChatLayout)
      commit('registry/REGISTRY_COMPONENT', Registry.ChatCloseButton)
    }

    const registry = pageComponents.flatMap((c) => c.registry).filter(Boolean)
    commit('registry/REGISTRY_COMPONENTS', registry)

    const searchResult = searchResultPromise ? await searchResultPromise : null

    if (searchResult?.components) {
      return { ...content, components: searchResult.components }
    }
    if (searchResult?.searchError) {
      return {
        ...content,
        theme,
        setThemeOnClient,
        components: pageComponents.map((component) => {
          if (
            [
              'sectionSearchVacancies',
              'sectionSearchVacanciesStartCareer',
            ].includes(component.type)
          ) {
            return {
              ...component,
              value: {
                ...component.value,
                initialListVacancies: [],
                listRegions: [],
                listSpecialties: [],
                initialTotalCountVacancies: 0,
                searchError: true,
              },
            }
          }
          return component
        }),
      }
    }

    return {
      ...content,
      theme,
      setThemeOnClient,
    }
  } catch (e) {
    error({ statusCode: e?.response?.status || 500 })
  }
}

const getAuxiliaryData = async (client: any, url: string, getters: any) => {
  if (process.client) {
    const cached = getters['pagesData/GET_AUXILIARY_DATA']
    if (cached) return cached
  }

  // На сервере: in-process кеш, чтобы избежать loopback HTTP на каждый запрос
  if (
    process.server &&
    auxiliaryDataCache &&
    auxiliaryDataCache.expireAt > Date.now()
  ) {
    return auxiliaryDataCache.data
  }

  const { data } = await client.get(url)

  if (process.server && data) {
    auxiliaryDataCache = { data, expireAt: Date.now() + AUXILIARY_CACHE_TTL }
  }

  return data
}

const getProstoData = async (ctx: Context, client?) => {
  const { store } = ctx
  const clientData = store.getters['sections/PROSTO_DATA']
  if (!clientData) {
    const httpClient = client || HTTP_REST_FRONTEND_API(ctx)
    const prostoData = await httpClient.get('/brand-media-data')
    store.commit('sections/SET_PROSTO_DATA', prostoData?.data || null)
    return prostoData?.data || {}
  }
  return clientData
}
